import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Grid,
    Button,
    IconButton,
    Card,
    CardContent,
    Toolbar, Tooltip,
    Dialog,
    TextField,
    FormControlLabel,
    Switch,
    Stack,
    Checkbox
} from "@mui/material";

import AsyncAutoCompleteMultiSelect from "../../components/AsyncAutoCompleteMultiSelect";
import { VariantViewModel, VariantCreate } from "../../Types/Item/VariantViewModel";
import { ItemViewModel } from "../../Types/Item/ItemViewModel";
import { Option, ListQuery } from "../../Types/CommonTypes";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import ColorSizeLengthInput from './ColorSizeLengthInput';
import { useLoading, useAuth, useSnackbar, useNavigation } from "../../Context/Contexts";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Save as SaveIcon, Close as CancelIcon, AddCard as AddCardIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import ResponsivePagination from "../ResponsivePagination";
import ListCardHeader from "../List/ListCardHeader";
import PaletteIcon from '@mui/icons-material/Palette';
import BlockIcon from "@mui/icons-material/Block";
interface VariantsProps {
    initItem: ItemViewModel;
    initialVariants: VariantViewModel[];
    createVariant: (variant: VariantCreate) => VariantViewModel;
    isCreatingNewItem: boolean;
    addVariantsToNewItem: (variant: VariantViewModel) => void;
    removeVariantsFromNewItem: (variant: VariantViewModel) => void;
}

const Variants: React.FC<VariantsProps> = ({
    initItem,
    initialVariants,
    createVariant,
    isCreatingNewItem,
    addVariantsToNewItem,
    removeVariantsFromNewItem
}) => {
    const { authService } = useAuth();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const { navigate } = useNavigation();
    const [isLoading, setIsLoading] = useState(false);
    const [item] = useState<ItemViewModel>(initItem);
    const [variants, setVariants] = useState<VariantViewModel[]>(initialVariants);
    const [selectedColorGroup, setSelectedColorGroup] = useState<{ value: string; description: string } | null>(null);
    const [selectedSizeGroup, setSelectedSizeGroup] = useState<{ value: string; description: string } | null>(null);
    const [selectedLengthGroup, setSelectedLengthGroup] = useState<{ value: string; description: string } | null>(null);
    const [colors, setColors] = useState<Option[]>([]);
    const [sizes, setSizes] = useState<Option[]>([]);
    const [lengths, setLengths] = useState<Option[]>([]);
    const [showAddColour, setShowAddColour] = useState(false);
    const [showAddSize, setShowAddSize] = useState(false);
    const [showAddLength, setShowAddLength] = useState(false);
    const [selectedColor, setSelectedColor] = useState<Option[]>([]);
    const [selectedSize, setSelectedSize] = useState<Option[]>([]);
    const [selectedLength, setSelectedLength] = useState<Option[]>([]);
    const [editingVariant, setEditingVariant] = useState<VariantViewModel | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [query, setQuery] = useState<ListQuery>({ page: 0, pageSize: 100, filter: "", filters: [] });
    const [pageSize, setPageSize] = useState(10);
    const { t } = useTranslation();

    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);


    useEffect(() => {
        setQuery({ ...query,
            page: page,
            pageSize: pageSize,
        });
    }, [page, pageSize]);



    //const fetchColorGroupOptions = async (
    //    search: string,
    //    offset: number,
    //    code?: string | null
    //): Promise<Option[]> => {
    //    const url = code ? `/autocompleteApi/ColorGroup?code=${code}` : `/autocompleteApi/ColorGroup?search=${search}&offset=${offset}`;
    //    const response = await authService.get<Option[]>(url);
    //    return response || [];
    //};

    //const fetchSizeGroupOptions = async (
    //    search: string,
    //    offset: number,
    //    code?: string | null
    //): Promise<Option[]> => {
    //    const url = `/autocompleteApi/SizeGroup?search=${search}&offset=${offset}`;
    //    const response = await authService.get<Option[]>(url);
    //    return response || [];
    //};

    //const fetchLengthGroupOptions = async (
    //    search: string,
    //    offset: number,
    //    code?: string | null
    //): Promise<Option[]> => {
    //    const url = `/autocompleteApi/LengthGroup?search=${search}&offset=${offset}`;
    //    const response = await authService.get<Option[]>(url);
    //    return response || [];
    //};

    const fetchColorOptions = async (
        search: string,
        offset: number,
        group?: string | null
    ): Promise<Option[]> => {
        const url = group
            ? `/autocompleteApi/Color?group=${group}&search=${search}&offset=${offset}`
            : `/autocompleteApi/Color?search=${search}&offset=${offset}`;
        const response = await authService.get<Option[]>(url);
        return response || [];
    };

    const fetchSizeOptions = async (
        search: string,
        offset: number,
        group?: string | null
    ): Promise<Option[]> => {
        const url = group
            ? `/autocompleteApi/Size?group=${group}&search=${search}&offset=${offset}`
            : `/autocompleteApi/Size?search=${search}&offset=${offset}`;
        const response = await authService.get<Option[]>(url);
        return response || [];
    };

    const fetchLengthOptions = async (
        search: string,
        offset: number,
        group?: string | null
    ): Promise<Option[]> => {
        const url = group
            ? `/autocompleteApi/Length?group=${group}&search=${search}&offset=${offset}`
            : `/autocompleteApi/Length?search=${search}&offset=${offset}`;
        const response = await authService.get<Option[]>(url);
        return response || [];
    };

    const initializeGroups = async () => {
        try {
            //const [colorGroupOptions, sizeGroupOptions, lengthGroupOptions] = await Promise.all([
            //    item.colorGroupCode
            //        ? fetchColorGroupOptions("", 0, item.colorGroupCode)
            //        : fetchColorGroupOptions("", 0),
            //    item.sizeGroupCode
            //        ? fetchSizeGroupOptions("", 0, item.sizeGroupCode)
            //        : fetchSizeGroupOptions("", 0),
            //    item.lengthGroupCode
            //        ? fetchLengthGroupOptions("", 0, item.lengthGroupCode)
            //        : fetchLengthGroupOptions("", 0),
            //]);

            //console.log("groups", [colorGroupOptions, sizeGroupOptions, lengthGroupOptions])
            //setSelectedColorGroup(
            //    item.colorGroupCode
            //        ? colorGroupOptions.find((opt) => opt.value === item.colorGroupCode) || null
            //        : null
            //);
            //setSelectedSizeGroup(
            //    item.sizeGroupCode
            //        ? sizeGroupOptions.find((opt) => opt.value === item.sizeGroupCode) || null
            //        : null
            //);
            //setSelectedLengthGroup(
            //    item.lengthGroupCode
            //        ? lengthGroupOptions.find((opt) => opt.value === item.lengthGroupCode) || null
            //        : null
            //);


            if (colors.length === 0) {
                const colors = await fetchColorOptions("", 0, item.colorGroupCode);
                setColors(colors);
            }

            if (sizes.length === 0) {
                const sizes = await fetchSizeOptions("", 0, item.sizeGroupCode);
                setSizes(sizes);
            }

            if (lengths.length === 0) {
                const lengths = await fetchLengthOptions("", 0, item.lengthGroupCode);
                setLengths(lengths);
            }
       
        } catch (error) {
            console.error("Error initializing groups:", error);
        }
    };




    const handleEditVariant = async(variant: VariantViewModel) => {
        setEditingVariant(variant);
        await initializeGroups();
    };


    const handleAddVariant = async () => {

        try {
            const newVariant = await createVariant({
                colourCode: selectedColor[0]?.value ?? "",
                colourDescription: selectedColor[0]?.description ?? "",
                sizeCode: selectedSize[0]?.value ?? "",
                sizeDescription: selectedSize[0]?.description ?? "",
                lengthCode: selectedLength[0]?.value ?? "",
                lengthDescription: selectedLength[0]?.description ?? "",
                colorGroup: selectedColorGroup?.value,
                sizeGroup: selectedSizeGroup?.value,
                lengthGroup: selectedLengthGroup?.value

            });

            setSelectedColor([]);
            setSelectedSize([]);
            setSelectedLength([]);

            setEditingVariant(newVariant);
            await initializeGroups();
        } catch (error) {
            console.error("Error adding variant:", error);
            showSnackbar(t("dialog.saveErrorGeneric"), "error");
        }
    };



    const handleSaveVariant = async () => {
        if (!editingVariant) return;
        if (!validateItem()) return;
        if (isCreatingNewItem) {
            // Store the new variant in the item until the item is saved
            addVariantsToNewItem(editingVariant);
            showSnackbar(t("dialog.savedSuccessfully"), "info");
            setEditingVariant(null);
            setVariants((prev) => {
                const exists = prev.some((row) => row.guid === editingVariant.guid);
                return exists
                    ? prev.map((row) => (row.guid === editingVariant.guid ? { ...editingVariant } : row))
                    : [...prev, editingVariant];
            });
            return;
        }
        try {
            setIsLoading(true);
            const response = await authService.post<VariantViewModel>("itemApi/CreateOrUpdateVariant", editingVariant);
            setVariants((prev) => {
                const updatedVariants = prev
                    .map((row) => (row.id === editingVariant.id ? { ...response } : row))
                    .filter((p) => p.id !== 0);

                return updatedVariants;
            });
            showSnackbar(t("dialog.variantSaved"), "success");
        } catch (error: any) {
            showSnackbar(`${t("dialog.variantSaveError")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const validateItem = () => {
        if (!editingVariant) {
            showSnackbar(t("dialog.variantAddNoSelection"), "error");
            return false;
        }

        if (colors.length > 0 && !editingVariant.colourCode) {
            showSnackbar(t("dialog.variantAddColorMissing"), "error");
            return false;
        }

        if (sizes.length > 0 && !editingVariant.sizeCode) {
            showSnackbar(t("dialog.variantAddSizeMissing"), "error");
            return false;
        }

        if (lengths.length > 0 && !editingVariant.lengthCode) {
            showSnackbar(t("dialog.variantAddLengthMissing"), "error");
            return false;
        }

        return true;
    };


    const handleDeleteVariant = async (variant: VariantViewModel) => {
        const isConfirmed = window.confirm(t("dialog.deleteItemConfirm"));
        if (!isConfirmed) return;
        if (isCreatingNewItem) {
            removeVariantsFromNewItem(variant);
            setVariants((prev) => {
                const updatedVariants = prev.filter((p) => p.guid !== variant.guid);
                return updatedVariants;
            });
            showSnackbar(t("dialog.variantRemoved"), "info");
            return;
        }
        setIsLoading(true);
        try {
            await authService.post("itemApi/DeleteVariant", variant);
            setVariants((prev) => {
                const updatedVariants = prev.filter((p) => p.id !== variant.id);
                return updatedVariants;
            });
            showSnackbar(t("dialog.variantDeleted"), "success");
        } catch (error: any) {
            console.error("Failed to delete variant:", error);
            showSnackbar(`${t("dialog.variantDeleteError")} : ${error.response?.data ?? error.message}`, "error");

        }
        finally {
            setIsLoading(false);
        }
    };

    const handleFieldChange = async (field: keyof VariantViewModel, value: any) => {
        if (editingVariant) {
            let updatedLine = { ...editingVariant, [field]: value }

            setEditingVariant((prev) => {
                if (!prev) return null;

                updatedLine = { ...prev, [field]: value };
                return updatedLine;
            });

        };
    }
    const handleGroupNavigation = (groupName: string) => {
        const isConfirmed = window.confirm(t("dialog.navigateToGroupConfirm", { groupName }));

        if (!isConfirmed) return;
        if (groupName === "Colour Group") navigate({ path: "/purchase/item/colourgroup/edit", params: { code: initItem.colorGroupCode } });
        if (groupName === "Size Group") navigate({ path: "/purchase/item/sizegroup/edit", params: { code: initItem.sizeGroupCode } });
        if (groupName === "Length Group") navigate({ path: "/purchase/item/lengthgroup/edit", params: { code: initItem.lengthGroupCode } });
        return;
    }


    return (
        <Box sx={{ padding: "1rem" }}>
      
                <Grid container spacing={2} justifyContent="center" marginBottom="1rem">
                    <Grid item xs={12} sm="auto">
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddVariant}
                        >
                            {`${t("common.add")} ${t("common.variant")}`}
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => handleGroupNavigation("Colour Group")}
                        >
                            {t("common.colourGroup")}
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => handleGroupNavigation("Size Group")}
                        >
                            {t("common.sizeGroup")}
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => handleGroupNavigation("Length Group")}
                        >
                            {t("common.lengthGroup")}
                        </Button>
                    </Grid>
                </Grid>
       


            <Grid container spacing={2} sx={{ mb: 2 }}>
                {variants.map((variant,index) => (
                    <Grid item
                        sm={variants.length === 1 ? 12 : variants.length === 2 ? 6 : 6}
                        md={variants.length === 1 ? 12 : variants.length === 2 ? 6 : 4}
                        key={variant.id}>

                        <Grid container spacing={1} sx={{ p: 1, border: '1px solid #ccc', borderRadius: 1, cursor: 'pointer', ':hover': { backgroundColor: '#f0f0f0' } }}>
                            <Grid item xs={10}>

                                <ListCardHeader
                                    icon={<PaletteIcon fontSize="small" color="action" />}
                                    title={variant.id}
                                    status={(variant.salesBlocked || variant.blocked)
                                        ? {
                                            label: variant.blocked ? t("common.blocked") : t("common.salesBlocked"),
                                            color: "warning",
                                            icon: <BlockIcon fontSize="small" />,
                                        }
                                        : undefined}
                                    hasEdit={true}
                                />
                                <Typography variant="body2" sx={{ color: "gray" }}>{variant.id}</Typography>

                                {variant.colourCode && (
                                    <Typography variant="body1">
                                        {t("common.colour")}: {variant.colourCode}{variant.colourDescription ? ` - ${variant.colourDescription}` : ""}
                                    </Typography>
                                )}

                                {variant.sizeCode && (
                                    <Typography variant="body1">
                                        {t("common.size")}: {variant.sizeCode}{variant.sizeDescription ? ` - ${variant.sizeDescription}` : ""}
                                    </Typography>
                                )}

                                {variant.lengthCode && (
                                    <Typography variant="body1">
                                        {t("common.length")}: {variant.lengthCode}{variant.lengthDescription ? ` - ${variant.lengthDescription}` : ""}
                                    </Typography>
                                )}

                                <Typography variant="body1">     {t("common.barcode")}: {variant.barcode}</Typography>
                                <Typography variant="body1">{t("common.supplierItemNo")}: {variant.supplierItemNo}</Typography>
                            </Grid>

                            <Grid item xs={2}>
                                <Stack spacing={1} direction="row" justifyContent="flex-end">
                                    <Tooltip title={t("common.edit")}>
                                        <IconButton size="small" color="primary" onClick={() => handleEditVariant(variant)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t("common.delete")}>
                                        <IconButton size="small" color="error" onClick={() => handleDeleteVariant(variant)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>

                ))}
            </Grid>

            <Dialog
                open={!!editingVariant}
                onClose={() => setEditingVariant(null)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: "8px", maxHeight: "80vh", overflowY: "auto" } }}
            >
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6">{t("common.editVariant")}</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={editingVariant?.isCreating ? 12 : 4}>
                            <AsyncAutoCompleteMultiSelect
                                label={t("common.colour")}
                                fetchOptions={fetchColorOptions}
                                initialOptions={colors}
                                value={colors.filter((c) => c.value === editingVariant?.colourCode)}
                                onChange={(selected) => {
                                    handleFieldChange("colourCode", selected[0]?.value || "")
                                    handleFieldChange("colourDescription", selected[0]?.description || "")
                                 }
                                }
                                disabled={!editingVariant?.isCreating}
                            />
         
                        </Grid>
                        {editingVariant?.isCreating && (
                            <>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={<Switch checked={showAddColour} onChange={(e) => setShowAddColour(e.target.checked)} />}
                                        label={t("common.addColour")}
                                    />
                                </Grid>
                                {showAddColour && (
                                    <Grid item xs={12}>
                                        <Box
                                            sx={{
                                                backgroundColor: "secondary.main",
                                                padding: 2,
                                                borderRadius: 2,
                                                width: "100%"
                                            }}
                                        >
                                        <ColorSizeLengthInput
                                                label={t("common.colour")}
                                            options={colors}
                                            setOptions={setColors}
                                            />
                                        </Box>
                                    </Grid>
                                )}
                            </>
                        )}
                        <Grid item xs={12} lg={editingVariant?.isCreating ? 12 : 4}>
                            <AsyncAutoCompleteMultiSelect
                                label={t("common.size")}
                                initialOptions={ sizes}
                                fetchOptions={fetchSizeOptions}
                                value={sizes.filter((s) => s.value === editingVariant?.sizeCode)}

                                onChange={(selected) =>
                                {
                                    handleFieldChange("sizeCode", selected[0]?.value || "")
                                    handleFieldChange("sizeDescription", selected[0]?.description || "")
                                }
                                }
                                disabled={!editingVariant?.isCreating}
                            />
                     
                        </Grid>
                        {editingVariant?.isCreating && (
                            <>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={<Switch checked={showAddSize} onChange={(e) => setShowAddSize(e.target.checked)} />}
                                        label={t("common.addSize")}
                                    />
                                </Grid>
                                {showAddSize && (
                            
                                    <Grid item xs={12} sx={{}}>
                                        <Box
                                            sx={{
                                                backgroundColor: "secondary.main",
                                                padding: 2,
                                                borderRadius: 2,
                                                width: "100%"
                                            }}
                                        >
                                        <ColorSizeLengthInput
                                                label={t("common.size")}
                                            options={sizes}
                                            setOptions={setSizes}
                                            />
                                        </Box>
                                    </Grid>
                                )}
                            </>
                        )}
                        <Grid item xs={12} lg={editingVariant?.isCreating ? 12 : 4}>
                            <AsyncAutoCompleteMultiSelect
                                label={t("common.length")}
                                fetchOptions={fetchLengthOptions}
                                initialOptions={lengths}
                                value={lengths.filter((l) => l.value === editingVariant?.lengthCode)}
                                onChange={(selected) =>
                                {
                                    handleFieldChange("lengthCode", selected[0]?.value || "")
                                    handleFieldChange("lengthDescription", selected[0]?.description || "")
                                }
                                }
                                disabled={!editingVariant?.isCreating}
                            />
                     
                        </Grid>
                        {editingVariant?.isCreating && (
                            <>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={<Switch checked={showAddLength} onChange={(e) => setShowAddLength(e.target.checked)} />}
                                        label={t("common.addLength")}
                                    />
                                </Grid>
                                {showAddLength && (
                                    <Grid item xs={12}>
                                        <Box
                                            sx={{
                                                backgroundColor: "secondary.main",
                                                padding: 2,
                                                borderRadius: 2,
                                                width: "100%"
                                            }}
                                        >
                                        <ColorSizeLengthInput
                                                label={t("common.length")}
                                            options={lengths}
                                            setOptions={setLengths}
                                            />
                                        </Box>
                                    </Grid>
                                )}
                            </>
                        )}
                        <Grid item xs={12} >
                            <TextField
                                label={t("common.barcode")}
                                value={editingVariant?.barcode}
                                onChange={(e) => handleFieldChange("barcode", e.target.value ?? null)}
                                fullWidth
                                margin="normal"
                                inputProps={{ maxLength: 50 }}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t("common.supplierItemNo")}
                                value={editingVariant?.supplierItemNo}
                                onChange={(e) => handleFieldChange("supplierItemNo", e.target.value ?? null)}
                                fullWidth
                                margin="normal"
                                inputProps={{ maxLength: 100 }}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={editingVariant?.blocked ?? false}
                                        onChange={(e) => handleFieldChange("blocked", e.target.checked)}
                                    />
                                }
                                label={t("common.blocked")}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={editingVariant?.salesBlocked ?? false}
                                        onChange={(e) => handleFieldChange("salesBlocked", e.target.checked)}
                                    />
                                }
                                label={t("common.salesBlocked")}
                            />
                        </Grid>
                    </Grid>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                        <Button onClick={handleSaveVariant} variant="contained" startIcon={<SaveIcon />}>
                            {t("common.save")}
                        </Button>
                        <Button onClick={() => setEditingVariant(null)} variant="outlined" startIcon={<CancelIcon />}>
                            {t("common.cancel")}
                        </Button>
                    </Box>
                </Box>
            </Dialog>
            <ResponsivePagination
                page={query.page}
                pageSize={query.pageSize}
                totalCount={totalCount}
                onPageChange={(newPage) => setQuery({ ...query, page: newPage })}
                sx={{ marginTop: "1rem" }}
            />
        </Box>
    );
};

export default Variants;